/**
 * Drawer focus-return registry — a module-level singleton (NOT React state).
 *
 * Records WHERE Escape-from-a-drawer's-tabs should send focus back to (the
 * table scroll container the user `→`-entered from). Only `useTableArrowNav`'s
 * `→`-enter writes a target, so the contract is purely data-driven: a return
 * target exists iff a table set one. Page-level/standalone tabs (Files, Red
 * Team) never call `setReturnTarget`, so they never get return behavior.
 *
 * Why a module singleton, not Context: the producer (`useTableArrowNav`, under
 * the table) and the consumer (the Drawer Esc handler, under `AppDrawers`) live
 * in different subtrees with no common ancestor below the app root. It holds a
 * single transient DOM ref, read once on Esc and cleared — no React state, so
 * no re-renders and no React-Compiler/identity concerns. Sits beside `scopes.ts`,
 * the existing module-level source of truth in this directory.
 *
 * PER-DRAWER OWNERSHIP (ENG-43, F5): `returnTarget` is the live slot, and
 * `savedSlots` is a stack snapshotting the slot at each drawer's open. On open a
 * drawer SAVES the current slot (`pushDrawerSlot`); on close it RESTORES that
 * saved slot (`popDrawerSlot`). The live slot is carried forward across an open
 * (not reset), so a target armed just before/at open is still honored by that
 * drawer's Esc. Crucially, a CHILD drawer's close pops back to the snapshot
 * taken when the child opened — i.e. the PARENT's stored table return-target —
 * instead of the old `clearReturnTarget()`-on-every-close, which wiped the
 * single global slot so the next Esc in the parent closed it rather than
 * returning focus to the table. Open/close are 1:1 (driven by the Drawer's
 * `wasOpenRef` once-guard), so the stack stays balanced.
 */
let returnTarget: HTMLElement | null = null;
const savedSlots: (HTMLElement | null)[] = [];

/**
 * A drawer is opening: snapshot the current live slot so the matching
 * `popDrawerSlot` on close can restore it. The live slot is intentionally NOT
 * reset — a target armed just before open stays honored by this drawer, while a
 * child's later close still restores this (parent) snapshot. Pairs 1:1 with
 * `popDrawerSlot`.
 */
export function pushDrawerSlot(): void {
  savedSlots.push(returnTarget);
}

/**
 * A drawer is closing: restore the slot snapshot taken when it opened, so a
 * child close preserves the parent's return target. The `?? null` guards an
 * unbalanced pop (e.g. a drawer that never registered open).
 */
export function popDrawerSlot(): void {
  returnTarget = savedSlots.pop() ?? null;
}

/** Record where Esc-from-tabs should send focus (the table scroll container). */
export function setReturnTarget(el: HTMLElement | null): void {
  returnTarget = el;
}

/** Read WITHOUT clearing — used by the Esc handler to decide return-vs-close. */
export function peekReturnTarget(): HTMLElement | null {
  // If the recorded element has left the DOM (table unmounted/virtualized away),
  // treat it as absent so Esc falls through to close.
  if (returnTarget && !returnTarget.isConnected) returnTarget = null;
  return returnTarget;
}

/** Read AND clear — used after focus is actually moved back to the table. */
export function consumeReturnTarget(): HTMLElement | null {
  const el = peekReturnTarget();
  returnTarget = null;
  return el;
}

/** Clear the topmost drawer's live slot (outer drawers' saved slots untouched). */
export function clearReturnTarget(): void {
  returnTarget = null;
}
